import apiV2 from '../services/apis/axiosClientV2'
import { MediaPurpose } from '../proto/generated/media'
import {
    FeedbackCategory,
    FeedbackMediaItem,
    MediaType,
    SubmitFeedbackRequest,
    SubmitFeedbackResponse
} from '../proto/generated/user-feedback'
import { FeedbackMediaAttachment } from '../reducer/feedback.reducer'

const CONTENT_TYPE: Record<FeedbackMediaAttachment['type'], string> = {
    screenshot: 'image/png',
    video: 'video/webm'
}

const FILE_EXT: Record<FeedbackMediaAttachment['type'], string> = {
    screenshot: 'png',
    video: 'webm'
}

async function getUploadUrl(fileName: string, contentType: string): Promise<{ uploadUrl: string; s3Key: string }> {
    const response = await apiV2.post('/media/upload-url', {
        fileName,
        contentType,
        purpose: MediaPurpose.FEEDBACK
    })
    return response.data
}

function uploadToS3(uploadUrl: string, blob: Blob, contentType: string, onProgress: (pct: number) => void): Promise<void> {
    return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest()
        xhr.open('PUT', uploadUrl)
        xhr.setRequestHeader('Content-Type', contentType)
        xhr.upload.onprogress = (e) => {
            if (e.lengthComputable) {
                onProgress(Math.round((e.loaded / e.total) * 100))
            }
        }
        xhr.onload = () => {
            if (xhr.status >= 200 && xhr.status < 300) {
                onProgress(100)
                resolve()
            } else {
                reject(new Error(`S3 upload failed with status ${xhr.status}`))
            }
        }
        xhr.onerror = () => reject(new Error('S3 upload network error'))
        xhr.send(blob)
    })
}

export interface UploadResult {
    s3Key: string
    type: FeedbackMediaAttachment['type']
}

const ATTACHMENT_MEDIA_TYPE: Record<FeedbackMediaAttachment['type'], MediaType> = {
    screenshot: MediaType.IMAGE,
    video: MediaType.VIDEO
}

export async function submitFeedback(
    category: FeedbackCategory,
    text: string,
    uploadResults: UploadResult[]
): Promise<SubmitFeedbackResponse> {
    const media: FeedbackMediaItem[] = uploadResults.map((r) =>
        FeedbackMediaItem.create({ s3Key: r.s3Key, type: ATTACHMENT_MEDIA_TYPE[r.type] })
    )
    const body = SubmitFeedbackRequest.toJSON(
        SubmitFeedbackRequest.create({ category, text, media })
    )
    const response = await apiV2.post('/feedback', body, {
        headers: { Accept: 'application/x-protobuf' },
        responseType: 'arraybuffer'
    })
    return SubmitFeedbackResponse.decode(new Uint8Array(response.data))
}

export async function getUploadKeys(attachments: FeedbackMediaAttachment[]): Promise<UploadResult[]> {
    const results: UploadResult[] = []
    for (const attachment of attachments) {
        const contentType = CONTENT_TYPE[attachment.type]
        const fileName = `${attachment.type}-${Date.now()}.${FILE_EXT[attachment.type]}`
        const { s3Key } = await getUploadUrl(fileName, contentType)
        results.push({ s3Key, type: attachment.type })
    }
    return results
}

export async function uploadAttachments(
    attachments: FeedbackMediaAttachment[],
    onProgress: (index: number, pct: number) => void
): Promise<UploadResult[]> {
    const results: UploadResult[] = []

    for (let i = 0; i < attachments.length; i++) {
        const attachment = attachments[i]
        const contentType = CONTENT_TYPE[attachment.type]
        const fileName = `${attachment.type}-${Date.now()}.${FILE_EXT[attachment.type]}`

        const { uploadUrl, s3Key } = await getUploadUrl(fileName, contentType)

        const blob = await fetch(attachment.url).then((r) => r.blob())

        await uploadToS3(uploadUrl, blob, contentType, (pct) => onProgress(i, pct))

        results.push({ s3Key, type: attachment.type })
    }

    return results
}
